import { useEffect, useContext, useState } from "react";
import React from "react";

import FuncionesContext, {
  getAtributo,
} from "../../../context/FuncionesContext";
import { useRefDataStore } from "../../../../..";

import Guardar from "../../../ABM/componentes/botonera/Guardar";
import Cancelar from "../../../ABM/componentes/botonera/Cancelar";

const sanitizeHtmlVanilla = (html) => {
  const allowedTags = new Set([
    "B",
    "I",
    "U",
    "STRONG",
    "EM",
    "P",
    "BR",
    "UL",
    "OL",
    "LI",
    "A",
    "SPAN",
    "DIV",
    "H1",
    "H2",
    "H3",
    "TABLE",
    "THEAD",
    "TBODY",
    "TH",
    "TD",
    "TR",
  ]);

  const allowedAttrs = new Set(["href", "target", "class", "style"]);

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const walk = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    if (!allowedTags.has(node.tagName)) {
      node.replaceWith(...Array.from(node.childNodes));
      return;
    }

    [...node.attributes].forEach((attr) => {
      if (!allowedAttrs.has(attr.name.toLowerCase())) {
        node.removeAttribute(attr.name);
      }

      if (
        attr.name.toLowerCase() === "href" &&
        attr.value.trim().toLowerCase().startsWith("javascript:")
      ) {
        node.removeAttribute(attr.name);
      }
    });

    [...node.children].forEach(walk);
  };

  [...doc.body.children].forEach(walk);

  return doc.body.innerHTML;
};

const autoResize = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
};

const DEFAULT_FONT_SIZE_REM = 0.8;
const FONT_SIZE_STEP_REM = 0.2;
const MIN_FONT_SIZE_REM = 0.6;
const MAX_FONT_SIZE_REM = 2;

const clampFontSizeRem = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_FONT_SIZE_REM;
  return Math.min(MAX_FONT_SIZE_REM, Math.max(MIN_FONT_SIZE_REM, parsed));
};

const parseFontSizeToRem = (rawSize = "") => {
  const size = rawSize.trim().toLowerCase();
  if (!size) return DEFAULT_FONT_SIZE_REM;

  if (size.endsWith("rem")) {
    return clampFontSizeRem(Number(size.replace("rem", "").trim()));
  }

  if (size.endsWith("px")) {
    const px = Number(size.replace("px", "").trim());
    return clampFontSizeRem(px / 16);
  }

  if (size.endsWith("em")) {
    return clampFontSizeRem(Number(size.replace("em", "").trim()));
  }

  return DEFAULT_FONT_SIZE_REM;
};

const createEmptyCell = () => ({
  text: "",
  bold: false,
  fontSizeRem: DEFAULT_FONT_SIZE_REM,
});

const createEmptyMatrix = (rows = 2, cols = 2) =>
  Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => createEmptyCell())
  );

const normalizeCell = (cell) => {
  if (cell && typeof cell === "object" && "text" in cell) {
    return {
      text: cell.text ?? "",
      bold: Boolean(cell.bold),
      fontSizeRem: clampFontSizeRem(cell.fontSizeRem),
    };
  }

  return {
    text: cell?.toString?.() ?? "",
    bold: false,
    fontSizeRem: DEFAULT_FONT_SIZE_REM,
  };
};

const escapeHtml = (text = "") =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const buildTableHtml = (matrix, hasHeader) => {
  if (!matrix?.length || !matrix[0]?.length) return "";

  const rowToHtml = (row, isHeader) => {
    const tag = isHeader ? "th" : "td";
    return `<tr>${row
      .map((rawCell) => {
        const cell = normalizeCell(rawCell);
        const style = [
          cell.bold ? "font-weight:700" : "",
          `font-size:${cell.fontSizeRem}rem`,
        ]
          .filter(Boolean)
          .join(";");
        return `<${tag} style="${style}">${escapeHtml(cell.text ?? "")}</${tag}>`;
      })
      .join("")}</tr>`;
  };

  if (!hasHeader) {
    return `<table><tbody>${matrix
      .map((row) => rowToHtml(row, false))
      .join("")}</tbody></table>`;
  }

  const [headerRow, ...bodyRows] = matrix;
  return `<table><thead>${rowToHtml(
    headerRow ?? [],
    true
  )}</thead><tbody>${bodyRows
    .map((row) => rowToHtml(row, false))
    .join("")}</tbody></table>`;
};

const parseTableHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html || "", "text/html");
  const table = doc.querySelector("table");

  if (!table) {
    return { matrix: createEmptyMatrix(2, 2), hasHeader: false };
  }

  const rows = Array.from(table.querySelectorAll("tr"));
  if (!rows.length) {
    return { matrix: createEmptyMatrix(2, 2), hasHeader: false };
  }

  const matrix = rows.map((row) =>
    Array.from(row.querySelectorAll("th, td")).map((cell) => {
      const styleValue = cell.getAttribute("style") || "";
      const fontWeightMatch = styleValue.match(/font-weight\s*:\s*([^;]+)/i);
      const fontSizeMatch = styleValue.match(/font-size\s*:\s*([^;]+)/i);
      const boldByWeight =
        !!fontWeightMatch &&
        (fontWeightMatch[1].trim() === "bold" ||
          Number(fontWeightMatch[1].trim()) >= 600);
      const boldByTag = cell.querySelector("strong, b") !== null;
      const sizeRaw = fontSizeMatch ? fontSizeMatch[1].trim() : "";
      const sizeRem = parseFontSizeToRem(sizeRaw);

      return {
        text: (cell.textContent || "").trim(),
        bold: boldByWeight || boldByTag,
        fontSizeRem: clampFontSizeRem(sizeRem),
      };
    })
  );

  const maxCols = Math.max(...matrix.map((row) => row.length), 1);
  const normalizedMatrix = matrix.map((row) => {
    const copy = [...row];
    while (copy.length < maxCols) copy.push(createEmptyCell());
    return copy;
  });

  const firstRowHasTh =
    rows[0]?.querySelectorAll("th")?.length > 0 ||
    table.querySelector("thead") !== null;

  return { matrix: normalizedMatrix, hasHeader: firstRowHasTh };
};

export default function EditableHtmlTable(props) {
  const {
    data,
    cab,
    campokey,
    id_elemento,
    valor,
    handleCancelar = () => null,
  } = props;

  const { superSubmit, validarRegex } = useContext(FuncionesContext);

  const [v, setStore] = useRefDataStore((s) => s[data._key + campokey]);
  const [update_id] = useRefDataStore(
    (s) => s[data._key + cab.update_id_alias]
  );

  const [value, setValue] = useState(v ?? valor);
  const [lastValue, setLastvalue] = useState(v ?? valor);
  const [isFocused, setIsFocused] = useState(false);
  const [guardado, setGuardado] = useState(true);
  const [updateId, setUpdateId] = useState(
    update_id ?? data[cab.update_id_alias]
  );
  const [tableMatrix, setTableMatrix] = useState(createEmptyMatrix(2, 2));
  const [hasHeader, setHasHeader] = useState(false);
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const [selectedCell, setSelectedCell] = useState({ row: 0, col: 0 });

  class handleGuardar {
    static update = async () => {
      let valor = value;
      if (valor === null || lastValue?.toString() === valor.toString().trim())
        return;

      const parsedHtml = new DOMParser().parseFromString(valor, "text/html")
        .body.innerHTML;

      // 🔹 2. Sanitizar HTML (vanilla)
      valor = sanitizeHtmlVanilla(parsedHtml).trim();

      if (!validarRegex(valor, cab.validacion_regex, cab)) {
        setValue(v);
        return;
      }

      const { id_a } = cab;
      setIsFocused(false);

      superSubmit({
        valor,
        id_a,
        update_id: updateId,
        handleCancelar,
        cab,
        data,
      })
        .then((result) => {
          setLastvalue(() => valor);
          setStore({ [data._key + campokey]: valor });
          if (!updateId) {
            setStore({
              [data._key + cab.update_id_alias]: result.data.registro.id,
            });
            setUpdateId(result.data.registro.id);
          }

          setGuardado(true);
          return result;
        })
        .catch((err) => {
          console.log("Cancelado ", err);
        });
    };
  }

  /**
   * Sync valor externo → DOM
   */
  useEffect(() => {
    if (isFocused) return;
    const currentValue = v ?? valor ?? "";
    setValue(currentValue);
    setLastvalue(currentValue);
    const parsed = parseTableHtml(currentValue);
    setTableMatrix(parsed.matrix);
    setHasHeader(parsed.hasHeader);
  }, [valor, v, isFocused]);

  useEffect(() => {
    if (!isFocused) return;
    const generatedHtml = buildTableHtml(tableMatrix, hasHeader).trim();
    setValue(generatedHtml);
    setGuardado(lastValue?.toString() === generatedHtml.toString());
  }, [tableMatrix, hasHeader, isFocused, lastValue]);

  const openBuilder = () => {
    const parsed = parseTableHtml(value ?? "");
    setTableMatrix(parsed.matrix);
    setHasHeader(parsed.hasHeader);
    setSelectedCell({ row: 0, col: 0 });
    setShowHtmlEditor(false);
    setIsFocused(true);
  };

  const updateCell = (rowIndex, colIndex, cellValue) => {
    setTableMatrix((prev) =>
      prev.map((row, rIdx) =>
        rIdx === rowIndex
          ? row.map((cell, cIdx) =>
              cIdx === colIndex
                ? {
                    ...normalizeCell(cell),
                    text: cellValue,
                  }
                : cell
            )
          : row
      )
    );
  };

  const updateSelectedCellStyle = (updater) => {
    setTableMatrix((prev) =>
      prev.map((row, rowIndex) =>
        row.map((cell, colIndex) => {
          if (
            rowIndex !== selectedCell.row ||
            colIndex !== selectedCell.col
          ) {
            return cell;
          }

          return updater(normalizeCell(cell));
        })
      )
    );
  };

  const toggleBoldSelected = () => {
    updateSelectedCellStyle((cell) => ({
      ...cell,
      bold: !cell.bold,
    }));
  };

  const changeSelectedFontSize = (delta) => {
    updateSelectedCellStyle((cell) => ({
      ...cell,
      fontSizeRem: clampFontSizeRem(
        Math.round((cell.fontSizeRem + delta) * 10) / 10
      ),
    }));
  };

  const addRow = () => {
    setTableMatrix((prev) => {
      const cols = prev[0]?.length || 1;
      const refRow = Math.min(selectedCell.row, prev.length - 1);
      const insertIndex = refRow + 1;
      const next = [...prev];
      next.splice(
        insertIndex,
        0,
        Array.from({ length: cols }, () => createEmptyCell())
      );
      return next;
    });
    setSelectedCell((prev) => ({
      ...prev,
      row: prev.row + 1,
    }));
  };

  const removeRow = () => {
    setTableMatrix((prev) => {
      if (prev.length <= 1) return prev;
      const rowToRemove = Math.min(selectedCell.row, prev.length - 1);
      return prev.filter((_, idx) => idx !== rowToRemove);
    });
    setSelectedCell((prev) => ({
      ...prev,
      row: Math.max(0, prev.row - 1),
    }));
  };

  const addColumn = () => {
    setTableMatrix((prev) => {
      const refCol = Math.min(selectedCell.col, (prev[0]?.length || 1) - 1);
      const insertIndex = refCol + 1;
      return prev.map((row) => {
        const next = [...row];
        next.splice(insertIndex, 0, createEmptyCell());
        return next;
      });
    });
    setSelectedCell((prev) => ({
      ...prev,
      col: prev.col + 1,
    }));
  };

  const removeColumn = () => {
    setTableMatrix((prev) => {
      if ((prev[0]?.length || 0) <= 1) return prev;
      const colToRemove = Math.min(selectedCell.col, prev[0].length - 1);
      return prev.map((row) => row.filter((_, idx) => idx !== colToRemove));
    });
    setSelectedCell((prev) => ({
      ...prev,
      col: Math.max(0, prev.col - 1),
    }));
  };

  const className = getAtributo("className", data, cab);
  const rowCount = tableMatrix.length;
  const colCount = tableMatrix[0]?.length || 0;
  const isEmptyValue =
    !value ||
    value
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim() === "";
  const selectedCellData =
    tableMatrix[selectedCell.row]?.[selectedCell.col] ?? createEmptyCell();
  const actionBtnStyle = {
    border: "1px solid #bdbdbd",
    background: "#eeeeee",
    color: "#222",
    borderRadius: "6px",
    padding: "0.25rem 0.55rem",
    cursor: "pointer",
    fontWeight: 600,
  };
  const neutralBtnStyle = {
    border: "1px solid #bdbdbd",
    background: "#f5f5f5",
    color: "#222",
    borderRadius: "6px",
    padding: "0.25rem 0.55rem",
    cursor: "pointer",
    fontWeight: 600,
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        border: guardado ? "none" : "2px solid red",
      }}
      id={id_elemento + "_container"}
      className={`${className}`}
    >
      {/* HTML editable */}
      {isFocused ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "0.4rem",
            }}
          >
            <button type="button" style={actionBtnStyle} onClick={addRow}>
              <span style={{ color: "#2e7d32", fontWeight: 800 }}>+</span> Fila
            </button>
            <button
              type="button"
              style={actionBtnStyle}
              onClick={removeRow}
              disabled={rowCount <= 1}
            >
              <span style={{ color: "#c62828", fontWeight: 800 }}>-</span> Fila
            </button>
            <button type="button" style={actionBtnStyle} onClick={addColumn}>
              <span style={{ color: "#2e7d32", fontWeight: 800 }}>+</span>{" "}
              Columna
            </button>
            <button
              type="button"
              style={actionBtnStyle}
              onClick={removeColumn}
              disabled={colCount <= 1}
            >
              <span style={{ color: "#c62828", fontWeight: 800 }}>-</span>{" "}
              Columna
            </button>
            <button
              type="button"
              style={{
                ...neutralBtnStyle,
                fontWeight: selectedCellData.bold ? 800 : 600,
              }}
              onClick={toggleBoldSelected}
            >
              B
            </button>
            <button
              type="button"
              style={neutralBtnStyle}
              onClick={() => changeSelectedFontSize(FONT_SIZE_STEP_REM)}
            >
              A+
            </button>
            <button
              type="button"
              style={neutralBtnStyle}
              onClick={() => changeSelectedFontSize(-FONT_SIZE_STEP_REM)}
            >
              A-
            </button>
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.2rem",
                fontSize: "0.9rem",
              }}
            >
              <input
                type="checkbox"
                checked={hasHeader}
                onChange={(e) => setHasHeader(e.target.checked)}
              />
              Primera fila como encabezado
            </label>
            <button
              type="button"
              onClick={() => setShowHtmlEditor((prev) => !prev)}
            >
              {showHtmlEditor ? "Ocultar HTML" : "Ver HTML"}
            </button>
            <span style={{ fontSize: "0.85rem" }}>
              Celda: F{selectedCell.row + 1} C{selectedCell.col + 1} |{" "}
              {selectedCellData.fontSizeRem.toFixed(1)}rem
            </span>
          </div>

          <div style={{ width: "100%" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                tableLayout: "fixed",
              }}
            >
              <tbody>
                {tableMatrix.map((row, rowIndex) => (
                  <tr key={`row-${rowIndex}`}>
                    {row.map((rawCell, colIndex) => {
                      const cell = normalizeCell(rawCell);
                      return (
                      <td
                        key={`cell-${rowIndex}-${colIndex}`}
                        style={{
                          border: "1px solid #ccc",
                          padding: "0.25rem",
                          width: `${100 / Math.max(colCount, 1)}%`,
                          background:
                            hasHeader && rowIndex === 0 ? "#f4f4f4" : "white",
                          boxShadow:
                            selectedCell.row === rowIndex &&
                            selectedCell.col === colIndex
                              ? "inset 0 0 0 2px #1976d2"
                              : "none",
                        }}
                      >
                        <input
                          type="text"
                          value={cell.text}
                          onFocus={() =>
                            setSelectedCell({ row: rowIndex, col: colIndex })
                          }
                          onClick={() =>
                            setSelectedCell({ row: rowIndex, col: colIndex })
                          }
                          onChange={(e) =>
                            updateCell(rowIndex, colIndex, e.target.value)
                          }
                          placeholder={
                            hasHeader && rowIndex === 0
                              ? `Encabezado ${colIndex + 1}`
                              : `Dato ${rowIndex + 1}-${colIndex + 1}`
                          }
                          style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            fontWeight: cell.bold ? 700 : 400,
                            fontSize: `${cell.fontSizeRem}rem`,
                            overflowWrap: "anywhere",
                          }}
                        />
                      </td>
                    );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showHtmlEditor && (
            <textarea
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setGuardado(lastValue !== e.target.value);
                autoResize(e.target);
              }}
              onBlur={(e) => {
                const parsed = parseTableHtml(e.target.value);
                setTableMatrix(parsed.matrix);
                setHasHeader(parsed.hasHeader);
              }}
              style={{
                width: "100%",
                minHeight: "120px",
                fontFamily: "monospace",
                fontSize: "12px",
              }}
            />
          )}
        </div>
      ) : (
        <div
          dangerouslySetInnerHTML={{
            __html: isEmptyValue ? "<span>Click para editar tabla</span>" : value,
          }}
          onClick={openBuilder}
          title="Click para editar tabla"
          style={{
            width: "100%",
            minHeight: "44px",
            border: isEmptyValue ? "1px dashed #9e9e9e" : "1px solid transparent",
            borderRadius: "6px",
            padding: "0.4rem",
            cursor: "pointer",
            color: isEmptyValue ? "#6f6f6f" : "inherit",
            transition: "border-color 0.15s ease, background-color 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#1976d2";
            e.currentTarget.style.backgroundColor = "#f8fbff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = isEmptyValue
              ? "#9e9e9e"
              : "transparent";
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        />
      )}

      {/* Botonera (TU BLOQUE, intacto) */}
      {(isFocused || !guardado) && (
        <div
          style={{ width: "100%", margin: "0.2rem" }}
          className="btns-noteTaker"
        >
          <Guardar
            handleSubmit={handleGuardar.update}
            texto_confirmar="Guardar"
            size="small"
            buttonId={id_elemento + "_submit"}
            submitOnEnter={false}
          />
          <Cancelar
            size="small"
            handleCancelar={() => {
              setValue(lastValue ?? valor ?? "");
              const parsed = parseTableHtml(lastValue ?? valor ?? "");
              setTableMatrix(parsed.matrix);
              setHasHeader(parsed.hasHeader);
              setSelectedCell({ row: 0, col: 0 });
              setGuardado(true);
              setIsFocused(false);
              if (typeof handleCancelar === "function") handleCancelar();
            }}
          />
        </div>
      )}
    </div>
  );
}
